import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { leadsApi } from '../../api/leads'

interface Lead { id: string; firstName: string; lastName: string; email: string }

interface Props {
  companyId: string
  value: Lead | null
  onChange: (lead: Lead | null) => void
  required?: boolean
  placeholder?: string
  hideEmail?: boolean
}

export function LeadSearchInput({ companyId, value, onChange, required, placeholder, hideEmail }: Props) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [allLeads, setAllLeads] = useState<Lead[]>([])
  const [hasMore, setHasMore] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  // Tracks which query string was last processed in the data effect so we can
  // detect when the query changed but React Query returned a same-reference
  // cached result (staleTime window) — in that case we still need to reset allLeads.
  const effectQueryRef = useRef('')

  const { data, isFetching } = useQuery({
    queryKey: ['leads-search', companyId, query, page],
    queryFn: () =>
      leadsApi.list(companyId, { search: query || undefined, limit: 10, page }).then(r => r.data),
    enabled: open,
    staleTime: 10_000,
  })

  // Accumulate pages; handles both new data AND same-reference cached data on query change.
  // `query` is in deps so the effect re-runs when query changes even if `data` didn't change.
  useEffect(() => {
    if (!data) return
    const newLeads = data.data as Lead[]
    const isNewQuery = effectQueryRef.current !== query
    effectQueryRef.current = query
    if (isNewQuery || page === 1) {
      setAllLeads(newLeads)
    } else {
      setAllLeads(prev => [...prev, ...newLeads])
    }
    setHasMore(newLeads.length === 10)
  }, [data, query])

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery)
    setPage(1)
    setAllLeads([]) // optimistically clear while new results load
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (!isFetching && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setPage(prev => prev + 1)
    }
  }

  if (value) {
    return (
      <div className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg bg-primary-50">
        <span className="text-xs font-medium text-primary-700">
          {value.firstName} {value.lastName}
          {!hideEmail && value.email && <span className="text-primary-400 ml-1">({value.email})</span>}
        </span>
        <button type="button" onClick={() => onChange(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-xs">×</button>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={query}
        onChange={e => handleQueryChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? 'Search leads by name or email…'}
        required={required}
        className="input w-full"
      />
      {open && (
        <div
          onScroll={handleScroll}
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {allLeads.map(lead => (
            <button
              key={lead.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
              onMouseDown={() => {
                onChange(lead)
                setQuery('')
                setOpen(false)
                setAllLeads([])
                setPage(1)
              }}
            >
              <span className="font-medium text-gray-900">{lead.firstName} {lead.lastName}</span>
              {!hideEmail && lead.email && <span className="text-gray-400 text-xs">{lead.email}</span>}
            </button>
          ))}
          {isFetching && (
            <div className="px-3 py-2 text-xs text-gray-400 text-center">Loading…</div>
          )}
          {!isFetching && allLeads.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              {query.length > 0 ? 'No leads found' : 'No leads'}
            </div>
          )}
          {!isFetching && !hasMore && allLeads.length > 0 && (
            <div className="px-3 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
              No more results
            </div>
          )}
        </div>
      )}
    </div>
  )
}
